import { motion } from "framer-motion";
import { useGlobalMarket } from "@/hooks/useGlobalMarket";
import { cn } from "@/lib/utils";
import { T } from "@/i18n/T";
import { useT } from "@/i18n/useLocale";

const fmtCompact = (n: number) => {
  if (!n) return "$0";
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  return `$${n.toLocaleString()}`;
};

const fgColor = (v?: number) => {
  if (v === undefined) return "text-muted-foreground";
  if (v >= 75) return "text-success";
  if (v >= 55) return "text-success/80";
  if (v >= 45) return "text-warning";
  if (v >= 25) return "text-warning/90";
  return "text-destructive";
};

interface ItemProps {
  label: string;
  value: React.ReactNode;
  delta?: number;
  sub?: React.ReactNode;
}

const Item = ({ label, value, delta, sub }: ItemProps) => (
  <div className="flex items-center gap-2 whitespace-nowrap">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="text-xs font-semibold text-foreground tabular-nums">{value}</span>
    {delta !== undefined && (
      <span className={cn("text-[11px] tabular-nums", delta >= 0 ? "text-success" : "text-destructive")}>
        {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(2)}%
      </span>
    )}
    {sub}
  </div>
);

const MarketStatsBanner = () => {
  const t = useT();
  const { data } = useGlobalMarket();

  return (
    <div className="sticky top-0 z-30 -mx-3 md:-mx-6 px-3 md:px-6 bg-background/85 backdrop-blur-md border-b border-border/60">
      <div className="flex items-center gap-5 overflow-x-auto py-2.5 scrollbar-none" style={{ scrollbarWidth: "none" }}>
        <div className="flex items-center gap-1.5 shrink-0">
          <motion.span
            className="h-2 w-2 rounded-full bg-success"
            animate={{ scale: [1, 1.4, 1], opacity: [1, 0.5, 1] }}
            transition={{ duration: 1.6, repeat: Infinity }}
          />
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium"><T k="auto.live-65c8" /></span>
        </div>

        <Item label={t("auto.cryptos-843d")} value={data?.active_cryptocurrencies?.toLocaleString() ?? "-"} />
        <span className="h-3 w-px bg-border/60" />
        <Item
          label={t("auto.market-cap-ff0a")}
          value={fmtCompact(data?.total_market_cap_usd ?? 0)}
          delta={data?.market_cap_change_percentage_24h_usd}
        />
        <span className="h-3 w-px bg-border/60" />
        <Item label={t("auto.24h-vol-67f5")} value={fmtCompact(data?.total_volume_24h_usd ?? 0)} />
        <span className="h-3 w-px bg-border/60" />
        <Item label={t("auto.dominance-bc2e")} value={
          <span>
            BTC {data?.btc_dominance?.toFixed(1) ?? "-"}% · ETH {data?.eth_dominance?.toFixed(1) ?? "-"}%
          </span>
        } />
        {data?.fearGreed && (
          <>
            <span className="h-3 w-px bg-border/60" />
            <Item
              label={t("auto.fear-greed-3bc6")}
              value={
                <span className={cn("font-semibold", fgColor(data.fearGreed.value))}>
                  {data.fearGreed.value} · {data.fearGreed.label}
                </span>
              }
            />
          </>
        )}
      </div>
    </div>
  );
};

export default MarketStatsBanner;
